import { BehaviorData } from "../behaviordata";
import { Color, Orientation } from "../common";

/** Type identifier for Video components */
export const VIDEO_TYPE = "Video" as const;

/**
 * Controls video playback settings
 *
 * @action play - Play the video
 * @action pause - Pause the video
 * @action stop - Stop playback and reset to the beginning
 * @action seek - Seek to a time in seconds. Args: (time, mode?) where mode is "absolute" (default) or "relative" — "relative" adds the delta to the current time, so seek(2, "relative") jumps 2s forward and seek(-2, "relative") jumps 2s back.
 * @action setVolume - Set video volume, 0-1 (takes volume argument)
 */
export interface VideoData extends BehaviorData {
  "@type": typeof VIDEO_TYPE;

  /**
   * Whether to automatically play video on scene start
   * @default true
   */
  playOnStart?: boolean;

  /**
   * Whether user can interact with video controls
   * @default false
   */
  userInteractible?: boolean;

  /**
   * Shows playback controls (i.e. timeline)
   * Also enables userInteractible (tap to play)
   * @default false
   */
  controls?: boolean;

  /**
   * Whether to loop video playback
   * @default true
   */
  loop?: boolean;

  /**
   * Video aspect ratio (width/height).
   * @step 0.01
   */
  // No @default — aspect is detected from video metadata on first load and
  // written back. Until metadata arrives, 1.33 (4:3) is used as a placeholder.
  aspect?: number;

  /**
   * Video orientation
   * @default "Vertical"
   */
  orientation?: Orientation;

  /**
   * How the video meets a box whose aspect differs from its own.
   *
   * `contain` (the default) letterboxes: the plane shrinks on one axis so
   * the whole frame stays visible inside the box, never distorted.
   *
   * `cover` fills: the plane spans the box exactly and a centered window
   * of the frame is shown instead, cropping the overflowing axis. The
   * crop is a view, not an edit — switching back to `contain` shows the
   * full frame again.
   *
   * Absent means `contain`: exactly the fit scenes have always had.
   * @default "contain"
   */
  fit?: 'contain' | 'cover';

  /**
   * Corner radius in meters (the unit MeshBackground's cornerRadius
   * uses; measured here on the CURRENT visible media rectangle - a
   * media quad has no rest box). One value gives differently sized
   * frames identical corners, and a resized frame keeps its corner
   * size. Never larger than half the rectangle's current smaller side
   * - an oversized value degrades to a pill instead of self-crossing.
   *
   * Rounds the media quad itself, which neither Bevel (its host gate
   * takes only builtin primitives) nor MeshBackground (drives the
   * library rigs' authored morphs) can reach. Composes with `fit`: the
   * corners round the letterboxed rectangle under `contain`, the full
   * box under `cover`. Strictly geometry: paint stays on Renderer,
   * sizing stays on the transform; clients without a corner-radius
   * implementation render the sharp rectangle - content intact.
   * @default 0
   * @min 0
   * @step 0.001
   */
  cornerRadius?: number;

  /**
   * Round the quad to half its smaller side — a capsule at any size,
   * which is the one shape an absolute radius cannot name. Overrides
   * `cornerRadius` when true. Same vocabulary as Bevel and
   * MeshBackground (#759).
   * @default false
   */
  pill?: boolean;

  /**
   * What the quad's back shows. `none` culls it: nothing is drawn behind
   * the picture. `picture` renders the media on both faces of the one
   * quad - seen from behind it reads mirrored, as a print does through
   * its paper (a two-sided quad; glTF's doubleSided idea, NOT a second
   * readable surface). `color` backs the picture with a flat unlit
   * `backColor` card that also shows through transparent pixels from the
   * front. A GLB export carries what the object shows: `picture` as the
   * doubled quad, `color` as a second mesh named `<quad>_back`.
   * @default "none"
   */
  back?: 'none' | 'picture' | 'color';

  /**
   * The card color behind the picture when `back` is `color`
   * (#RRGGBB or #RRGGBBAA - the alpha rides the string). Ignored for
   * the other values.
   * @default "#FFFFFF"
   */
  backColor?: Color;
}
